import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CameraIcon, VideoCameraIcon, TrashIcon, PlusIcon, CheckCircleIcon, ArrowPathIcon, SparklesIcon, PaperClipIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import './Media.css';
import { isActivityOngoing } from './Activity';
import { IDLE_COMPLETION, SUBMIT_NOUNS, SubmitFeedbackAnnouncement, SubmitFeedbackButton } from './SubmitFeedback';
import { convertImageToJpg } from '../../utils/imageUtils';
import { API_BASE_URL } from '../../config';
import { getPhotoEligibility, getFilePhotoEvidence, arePhotoFilesEligible, resolvePhotoValidationContract as resolveMediaRequirementContract } from './mediaPhotoEligibility';
import VideoThumbnail from './VideoThumbnail';
import MediaGalleryStage from './MediaGalleryStage';
import { sanitizeBodyEstimates } from './bodyEstimateEligibility';
import MediaCoachChat from './MediaCoachChat';
import { connectAuthenticatedMediaSocket } from './mediaAuthenticatedSocket';
import {
  abandonCanonicalRun,
  beginCanonicalRun,
  confirmCanonicalRun,
  releaseCanonicalRun,
  waitForCanonicalRun,
  withRequestDeadline,
} from './mediaCanonicalRun';

export const acceptInferenceEvent = (activeRuns, message) => {
  if (!message?.fileId) return true;
  if (!message.run_id) return !activeRuns[message.fileId];
  const current = activeRuns[message.fileId];
  const eventStartedAt = Date.parse(message.run_started_at || '') || 0;
  if (message.status === 'started') {
    if (current && current.startedAt > eventStartedAt) return false;
    if (current?.finished && current.runId === message.run_id) return false;
    activeRuns[message.fileId] = { runId: message.run_id, startedAt: eventStartedAt };
    return true;
  }
  if (current && current.runId !== message.run_id && current.startedAt >= eventStartedAt) return false;
  if (message.status === 'completed' || message.status === 'failed') {
    // Retain the last run's fence after completion; otherwise a late old
    // terminal event can become the latest visible verdict/approval again.
    activeRuns[message.fileId] = { runId: message.run_id, startedAt: eventStartedAt, finished: true };
  }
  return true;
};

const validRemovalActivity = (activity, activityId) => String(activity?._id) === String(activityId)
  && Array.isArray(activity?.data?.files);
const containsRemovedFile = (activity, fileId) => activity.data.files.some(file => String(file?._id) === String(fileId));

// The server may withdraw the descriptor before storage cleanup/response fails.
// One authorized read resolves that uncertainty; never retry the mutation here.
const removeAndConfirm = async (service, activityId, fileId, isCurrent) => {
  try {
    const activity = await service.removeMediaFile(activityId, fileId);
    if (!isCurrent()) return null;
    if (validRemovalActivity(activity, activityId) && !containsRemovedFile(activity, fileId)) return { activity, removed: true };
  } catch (_) { /* Reconcile a potentially committed removal below. */ }
  if (!isCurrent() || typeof service.fetchActivity !== 'function') return null;
  try {
    const activity = await withRequestDeadline(service.fetchActivity(activityId, { timeout: 15000 }), 15000, 'Removal refresh timed out.');
    if (!isCurrent() || !validRemovalActivity(activity, activityId)) return null;
    return { activity, removed: !containsRemovedFile(activity, fileId) };
  } catch (_) { return null; }
};

const incompleteAnalysisReason = getPhotoEligibility(null).reason;

// Presentation only: canonical eligibility remains the fail-closed source of credit.
const describePhoto = (file, evidence, status, constraints = []) => {
  if (evidence.status === 'earned') {
    // Saved credit and the latest analysis are separate facts. Legacy approvals
    // predate per-photo verdicts; do not invent a missing historical verdict.
    const validation = file.inferences?.at(-1)?.result?.photo_validation;
    const currentStatus = status || file.inferenceStatus;
    let reasons = [];
    if (validation?.version !== undefined || ['started', 'processing', 'failed'].includes(currentStatus)) {
      const latestFile = { ...file, inferenceStatus: currentStatus };
      const latest = describePhoto(latestFile, getFilePhotoEvidence(latestFile, constraints), currentStatus, constraints);
      if (!evidence.eligible || latest.kind !== 'accepted') reasons = [`Latest analysis: ${latest.label}`, ...latest.reasons];
    }
    return { kind: evidence.eligible ? 'accepted' : 'historical', label: evidence.eligible ? 'Accepted (saved approval)' : 'No saved approval', reasons };
  }
  if (evidence.eligible) return { kind: 'accepted', label: 'Accepted', reasons: [] };
  if (file.isPlaceholder || status === 'started' || file.inferenceStatus === 'processing') {
    return { kind: 'processing', label: file.isPlaceholder ? 'Uploading' : 'Processing', reasons: ['Wait for photo analysis to finish.'] };
  }
  if (status === 'failed' || file.inferenceStatus === 'failed') {
    return { kind: 'incomplete', label: 'Analysis failed', reasons: ['Analysis did not finish. This is not a photo rejection. View details to analyze again.'] };
  }
  if (!file.inferences?.length && arePhotoFilesEligible([file], constraints)) {
    return { kind: 'unanalyzed', label: 'No analysis recorded', reasons: ['No verified view coverage from this photo. Extras without analysis are allowed when no all-photo criteria are configured.'] };
  }
  const validation = file.inferences?.at(-1)?.result?.photo_validation;
  const malformed = validation && (!Array.isArray(validation.failed_checks)
    || !validation.failed_checks.every(reason => typeof reason === 'string' && reason.trim())
    || (validation.global_constraints !== undefined && (!Array.isArray(validation.global_constraints)
      || !validation.global_constraints.every(check => check && typeof check.reason === 'string' && check.reason.trim()))));
  if (evidence.reason === incompleteAnalysisReason || malformed) {
    return { kind: 'incomplete', label: 'Analysis incomplete', reasons: ['Photo criteria could not be verified: the analysis did not provide a complete, valid photo verdict. This is not a photo rejection. View details to analyze again.'] };
  }
  const reasons = [...new Set([
    ...(Array.isArray(validation?.failed_checks) ? validation.failed_checks : []),
    ...(Array.isArray(validation?.global_constraints) ? validation.global_constraints : []).filter(check => check && ['fail', 'unverifiable'].includes(check.status)).map(check => check.reason),
    evidence.reason,
  ].filter(reason => typeof reason === 'string' && reason.trim()).map(reason => reason.trim()))];
  return { kind: 'attention', label: evidence.status === 'fail' ? 'Needs attention — Photo not accepted' : 'Needs attention — Photo not verified', reasons };
};

const Media = ({
  media,
  finishActivity,
  completion = IDLE_COMPLETION,
  backendService,
  modalService,
  readOnly = false,
  coachImageUrl = null,
  coachEmoji = '🥋',
  timezone = null,
}) => {
  const [files, setFiles] = useState(media.data.files || []);
  const [fileUrls, setFileUrls] = useState({});
  const [loadingFiles, setLoadingFiles] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [removingFileId, setRemovingFileId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [removalNotice, setRemovalNotice] = useState('');
  const removalPendingRef = useRef(null);
  const removalScopeRef = useRef(null);
  const ownerId = backendService.getUser?.()?._id ?? null;
  const removalScope = { activityId: media._id, backendService, readOnly, ownerId, status: media.status, dueAt: media.due_at, isTemplate: media.is_template, activityOwner: media.user_id };
  if (!removalScopeRef.current || Object.keys(removalScope).some(key => removalScope[key] !== removalScopeRef.current[key])) removalScopeRef.current = removalScope;
  const canRemovePhotos = !readOnly && !media.is_template && isActivityOngoing(media)
    && (ownerId == null || media.user_id == null || String(ownerId) === String(media.user_id));

  useEffect(() => {
    setFiles(media.data.files || []);
  }, [media._id, media.data.files]);

  useEffect(() => {
    setRemoveError(null);
    setRemovalNotice('');
    setRemovingFileId(null);
    removalPendingRef.current = null;
  }, [media._id, backendService, readOnly, ownerId, media.status, media.due_at, media.is_template, media.user_id]);

  const removePhoto = async (file) => {
    if (!canRemovePhotos || file?.isPlaceholder || removalPendingRef.current) return false;
    if (!window.confirm('Remove this attachment? This cannot be undone.')) return false;
    const scope = removalScopeRef.current;
    const token = {};
    const isCurrent = () => mediaMountedRef.current && scope === removalScopeRef.current
      && (backendService.getUser?.()?._id ?? null) === scope.ownerId;
    if (!isCurrent()) return false;
    removalPendingRef.current = token;
    setRemovingFileId(file._id);
    setRemoveError(null);
    try {
      const outcome = await removeAndConfirm(backendService, media._id, file._id, isCurrent);
      if (!isCurrent()) return false;
      if (!outcome) {
        setRemoveError({ fileId: file._id, message: 'Removal status could not be confirmed. Refresh this activity before trying again.' });
        return false;
      }
      setFiles(outcome.activity.data.files);
      if (!outcome.removed) {
        setRemoveError({ fileId: file._id, message: 'Could not remove this photo. Please try again.' });
        return false;
      }
      setRemovalNotice('Photo removal confirmed.');
      return true;
    } finally {
      if (removalPendingRef.current === token) removalPendingRef.current = null;
      if (isCurrent()) setRemovingFileId(null);
    }
  };
  const [inferenceStatus, setInferenceStatus] = useState(() => {
    const status = {};
    if (media.data.files) {
      media.data.files.forEach(f => {
        if (f.inferenceStatus === 'processing') {
          status[f._id] = 'started';
        } else if (f.inferenceStatus === 'failed') {
          status[f._id] = 'failed';
        } else if (f.inferenceStatus === 'completed') {
          status[f._id] = 'completed';
        }
      });
    }
    return status;
  });
  const [recentlyCompleted, setRecentlyCompleted] = useState({});
  // Bumped whenever the owner's canonical Coach thread may have moved on. The
  // chat re-reads the server rather than rendering anything from the socket.
  const [coachRefreshKey, setCoachRefreshKey] = useState(0);
  const [requirements, setRequirements] = useState(() => resolveMediaRequirementContract(media.data).views);
  const [globalConstraints, setGlobalConstraints] = useState(() => resolveMediaRequirementContract(media.data).globalConstraints);
  const [newRequirement, setNewRequirement] = useState('');
  const [editingRequirementIndex, setEditingRequirementIndex] = useState(null);
  const [editingRequirement, setEditingRequirement] = useState('');
  const [savingRequirement, setSavingRequirement] = useState(false);
  const [trackedMetrics, setTrackedMetrics] = useState(media.data.tracked_metrics || []);
  const [newTrackedMetric, setNewTrackedMetric] = useState('');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const activeInferenceRunsRef = useRef({});
  const canonicalRunRegistryRef = useRef({});
  const mediaMountedRef = useRef(true);
  const listeners = useRef([]);
  const maxUploadSize = parseInt(process.env.REACT_APP_NGINX_CLIENT_MAX_BODY_SIZE || String(50 * 1024), 10) * 1024 * 1024; // Default to 50GB

  useEffect(() => {
    const contract = resolveMediaRequirementContract({
      requirements: media.data?.requirements,
      global_constraints: media.data?.global_constraints,
    });
    setRequirements(contract.views);
    setGlobalConstraints(contract.globalConstraints);
    setTrackedMetrics(media.data?.tracked_metrics || []);
    setEditingRequirementIndex(null);
    setEditingRequirement('');
  }, [media._id, media.data?.requirements, media.data?.global_constraints, media.data?.tracked_metrics]);

  const persistRequirements = async (nextRequirements) => {
    const updatedMedia = await backendService.updateMediaRequirements(media._id, nextRequirements);
    const canonical = resolveMediaRequirementContract(updatedMedia?.data || {
      requirements: nextRequirements,
      global_constraints: globalConstraints
    });
    setRequirements(canonical.views);
    setGlobalConstraints(canonical.globalConstraints);
    return canonical.views;
  };

  const handleAddRequirement = async () => {
    if (!newRequirement.trim()) return;
    try {
      await persistRequirements([...requirements, newRequirement.trim()]);
      setNewRequirement('');
    } catch (e) {
      console.error("Failed to add requirement", e);
      alert("Failed to add requirement");
    }
  };

  const handleDeleteRequirement = async (index) => {
    try {
      await persistRequirements(requirements.filter((_, i) => i !== index));
      if (editingRequirementIndex === index) {
        setEditingRequirementIndex(null);
        setEditingRequirement('');
      }
    } catch (e) {
        console.error("Failed to delete requirement", e);
        alert("Failed to delete requirement");
    }
  };

  const startRequirementEdit = (index) => {
    setEditingRequirementIndex(index);
    setEditingRequirement(requirements[index] || '');
  };

  const cancelRequirementEdit = () => {
    setEditingRequirementIndex(null);
    setEditingRequirement('');
  };

  const saveRequirementEdit = async () => {
    const value = editingRequirement.trim();
    if (editingRequirementIndex == null || !value || savingRequirement) return;
    const next = requirements.map((requirement, index) => index === editingRequirementIndex ? value : requirement);
    setSavingRequirement(true);
    try {
      await persistRequirements(next);
      cancelRequirementEdit();
    } catch (error) {
      console.error('Failed to edit requirement', error);
      alert('Failed to edit requirement');
    } finally {
      setSavingRequirement(false);
    }
  };

  const handleAddTrackedMetric = async () => {
    if (!newTrackedMetric.trim()) return;
    const updated = [...trackedMetrics, newTrackedMetric.trim()];
    try {
      await backendService.updateMediaMetrics(media._id, updated);
      setTrackedMetrics(updated);
      setNewTrackedMetric('');
    } catch (e) {
      console.error("Failed to add tracked metric", e);
      alert("Failed to add tracked metric");
    }
  };

  const handleDeleteTrackedMetric = async (index) => {
    const updated = trackedMetrics.filter((_, i) => i !== index);
    try {
      await backendService.updateMediaMetrics(media._id, updated);
      setTrackedMetrics(updated);
    } catch (e) {
        console.error("Failed to delete tracked metric", e);
        alert("Failed to delete tracked metric");
    }
  };

  const hasPhotoRequirements = requirements.length > 0 || globalConstraints.length > 0;
  const photoEvidence = files.map(file => getFilePhotoEvidence(file, globalConstraints, media.status));
  const isRequirementMet = req => photoEvidence.some(photo => photo.matches.includes(req));

  const getRequirementRejectionReason = (req) => {
    for (let fileIndex = files.length - 1; fileIndex >= 0; fileIndex -= 1) {
      const inferences = files[fileIndex].inferences || [];
      const result = inferences[inferences.length - 1]?.result;
      if (!photoEvidence[fileIndex].eligible && (result?.attempted_requirement === req
        || result?.matched_requirement === req || result?.matched_requirements?.includes(req))) {
        const description = describePhoto(files[fileIndex], photoEvidence[fileIndex], inferenceStatus[files[fileIndex]._id], globalConstraints);
        return { reason: description.reasons.join(' '), rejected: description.kind === 'attention' && photoEvidence[fileIndex].status === 'fail' };
      }
      if (
        result?.matched_requirement == null
        && result?.attempted_requirement === req
        && typeof result?.requirement_rejection_reason === 'string'
        && result.requirement_rejection_reason.trim()
      ) {
        return { reason: result.requirement_rejection_reason.trim(), rejected: true };
      }
    }
    return null;
  };

  useEffect(() => {
    mediaMountedRef.current = true;
    return () => { mediaMountedRef.current = false; };
  }, []);

  const registerListener = useCallback((listener) => {
    listeners.current.push(listener);
    return () => {
      listeners.current = listeners.current.filter(l => l !== listener);
    };
  }, []);

  useEffect(() => {
    let wsUrl;
    if (API_BASE_URL) {
      const apiUrl = new URL(API_BASE_URL || window.location.origin);
      const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${apiUrl.host}/ws`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws`;
    }

    const connection = connectAuthenticatedMediaSocket({
      url: wsUrl,
      backendService,
      // A ready event can land during a disconnect or while fresh credentials
      // are resolving. Reconcile only once the server confirms this owner.
      onAuthenticated: () => setCoachRefreshKey(current => current + 1),
      onMessage: message => {
        if (message.type === 'media_coach_thread_updated' && message.activityId === media._id) {
          setCoachRefreshKey(current => current + 1);
          return;
        }
        if (message.type === 'inference_update' && message.activityId === media._id) {
          if (!acceptInferenceEvent(activeInferenceRunsRef.current, message)) return;
          setInferenceStatus(prev => ({
            ...prev,
            [message.fileId]: message.status
          }));
          // A terminal analysis result is exactly when the Coach note becomes
          // possible, so re-read the thread instead of waiting for a poll.
          if (message.status === 'completed' || message.status === 'failed') {
            setCoachRefreshKey(current => current + 1);
          }

          listeners.current.forEach(listener => listener(message));

          if (message.status === 'started') {
            setFiles(prevFiles => prevFiles.map(f => {
              if (f._id === message.fileId) {
                return {
                  ...f,
                  inferenceStatus: 'processing',
                  _activeInferenceRunId: message.run_id || f._activeInferenceRunId,
                };
              }
              return f;
            }));
          } else if (message.status === 'completed') {
            setRecentlyCompleted(prev => ({ ...prev, [message.fileId]: true }));
            setTimeout(() => {
              setRecentlyCompleted(prev => ({ ...prev, [message.fileId]: false }));
            }, 5000);

            setFiles(prevFiles => prevFiles.map(f => {
              if (f._id === message.fileId) {
                const newInference = {
                  result: message.data,
                  created_at: new Date()
                };
                return {
                  ...f,
                  inferences: [...(f.inferences || []), newInference],
                  inferenceStatus: 'completed',
                  _activeInferenceRunId: null,
                };
              }
              return f;
            }));
          } else if (message.status === 'failed') {
            setFiles(prevFiles => prevFiles.map(f => {
              if (f._id === message.fileId) {
                return { ...f, inferenceStatus: 'failed', _activeInferenceRunId: null };
              }
              return f;
            }));
          }
        }
      },
    });

    return () => connection.close();
  }, [backendService, media._id]);

  useEffect(() => {
    const fetchFileUrls = async () => {
      // Only fetch URLs for files that don't have one yet and aren't currently loading
      const filesToFetch = files.filter(file => 
        !file.isPlaceholder && !fileUrls[file._id] && !loadingFiles[file._id]
      );

      if (filesToFetch.length === 0) return;

      // Mark these specific files as loading
      setLoadingFiles(prev => {
        const next = { ...prev };
        filesToFetch.forEach(f => next[f._id] = true);
        return next;
      });

      const fetchPromises = filesToFetch.map(async (file) => {
        try {
          const url = await backendService.fetchMediaFileUrl(media._id, file._id, '1mp');
          return { id: file._id, url };
        } catch (error) {
          console.error(`Error fetching URL for file ${file._id}:`, error);
          return { id: file._id, url: null };
        }
      });

      const results = await Promise.all(fetchPromises);
      
      setFileUrls(prev => {
        const next = { ...prev };
        results.forEach(({ id, url }) => {
          if (url) next[id] = url;
        });
        return next;
      });

      setLoadingFiles(prev => {
        const next = { ...prev };
        results.forEach(({ id }) => {
          next[id] = false;
        });
        return next;
      });
    };

    if (files.length > 0) {
      fetchFileUrls();
    }
  }, [files, backendService, media._id, fileUrls, loadingFiles]);

  const applyCanonicalFiles = useCallback((activity, runId, runFileIds) => {
    if (!mediaMountedRef.current) return;
    const refreshedFiles = activity?.data?.files || [];
    const runFileIdSet = new Set(runFileIds.map(String));
    setFiles(refreshedFiles);
    setInferenceStatus(prev => {
      const next = { ...prev };
      refreshedFiles.forEach(file => {
        next[file._id] = file.inferenceStatus === 'processing' ? 'started' : file.inferenceStatus;
      });
      return next;
    });
    // An open modal listens on the same channel the WebSocket feeds, so the
    // canonical read refreshes it too instead of stranding it on stale results.
    refreshedFiles.forEach(file => {
      if (!runFileIdSet.has(String(file._id))) return;
      const latestResult = file.inferences?.[file.inferences.length - 1]?.result;
      const completedForRun = latestResult?._session?.run_id === runId;
      if (!completedForRun && file.inferenceStatus !== 'failed') return;
      listeners.current.forEach(listener => listener({
        type: 'inference_update',
        activityId: media._id,
        fileId: file._id,
        status: file.inferenceStatus,
        run_id: runId,
        data: completedForRun ? latestResult : undefined,
      }));
    });
  }, [media._id]);

  // Upload results used to depend entirely on the `inference_update` socket:
  // a missed or early event left the grid, the analysis and the requirement
  // checklist stale until a manual reload. Poll the canonical activity for the
  // exact run this upload started, bounded and owned like the rerun path.
  const reconcileUploadRun = useCallback((uploadResponse, knownFileIds, reservation) => {
    const runId = uploadResponse?.inference_run_id || uploadResponse?.data?.inference_run?.run_id;
    const runFileIds = (uploadResponse?.data?.files || [])
      .filter(file => file?.type?.startsWith('image')
        && file.inferenceStatus === 'processing'
        && !knownFileIds.has(String(file._id)))
      .map(file => String(file._id));
    if (!runId || runFileIds.length === 0) {
      abandonCanonicalRun(
        canonicalRunRegistryRef,
        reservation.key,
        reservation.pending,
        reservation.previous,
      );
      return;
    }

    setInferenceStatus(prev => {
      const next = { ...prev };
      runFileIds.forEach(fileId => { next[fileId] = 'started'; });
      return next;
    });

    const { key, pending } = reservation;
    const owner = confirmCanonicalRun(canonicalRunRegistryRef, key, pending, runId);
    if (!owner) return;
    waitForCanonicalRun({
      activityId: media._id,
      owner,
      registryRef: canonicalRunRegistryRef,
      backendService,
      applyActivity: (activity) => applyCanonicalFiles(activity, runId, runFileIds),
      fileIds: runFileIds,
      // Do not cancel fallback polling solely because a terminal WebSocket
      // event arrived. It can arrive before the upload response adds the new
      // file to React state, in which case the fast-path map update is a no-op.
      // The canonical read is what makes that ordering recoverable.
      isActive: () => mediaMountedRef.current,
    })
      .catch(error => console.error('Failed to reconcile uploaded media analysis:', error))
      .finally(() => releaseCanonicalRun(canonicalRunRegistryRef, key, owner));
  }, [applyCanonicalFiles, backendService, media._id]);

  const handleFileUpload = async (event) => {
    const uploadedFiles = Array.from(event.target.files);
    if (uploadedFiles.length === 0) return;

    // Check file sizes
    const oversizedFiles = uploadedFiles.filter((file) => file.size > maxUploadSize);
    if (oversizedFiles.length > 0) {
      alert(`Some files exceed the maximum upload size of ${maxUploadSize / (1024 * 1024 * 1024)}GB.`);
      return;
    }

    // Captured before the placeholders land so the canonical run only waits on
    // the images this upload actually added.
    const knownFileIds = new Set(files.filter(file => !file.isPlaceholder).map(file => String(file._id)));
    // Reserve activity ownership before the request starts. Otherwise an old
    // poll whose read is already in flight can apply after this upload has
    // begun but before its response reveals the new run id.
    const canonicalReservation = beginCanonicalRun(canonicalRunRegistryRef, media._id);

    setIsUploading(true); // Block new uploads
    const placeholderFiles = uploadedFiles.map((file, index) => ({
      _id: `placeholder-${Date.now()}-${index}`,
      name: file.name,
      type: file.type,
      isPlaceholder: true,
    }));
    setFiles((prevFiles) => [...prevFiles, ...placeholderFiles]);

    const processedFiles = await Promise.all(uploadedFiles.map(async (file) => {
      try {
        return await convertImageToJpg(file);
      } catch (error) {
        console.error("Error converting file to JPG:", error);
        return file;
      }
    }));

    const formData = new FormData();
    processedFiles.forEach((file) => formData.append('files', file));

    try {
      const updatedMedia = await backendService.uploadMediaFiles(media._id, formData);
      setFiles(updatedMedia.data.files);
      reconcileUploadRun(updatedMedia, knownFileIds, canonicalReservation);
    } catch (error) {
      abandonCanonicalRun(
        canonicalRunRegistryRef,
        canonicalReservation.key,
        canonicalReservation.pending,
        canonicalReservation.previous,
      );
      console.error('Error uploading files:', error);
      alert('Failed to upload files: ' + error.message);
      setFiles((prevFiles) => prevFiles.filter((file) => !file.isPlaceholder));
    } finally {
      setIsUploading(false); // Allow new uploads
      event.target.value = '';
    }
  };

  const handleMediaClick = (fileIndex) => {
    const openingScope = removalScopeRef.current;
    const ownsOrigin = () => mediaMountedRef.current && openingScope === removalScopeRef.current
      && (backendService.getUser?.()?._id ?? null) === openingScope.ownerId;
    const initialMediaItems = (files || []).map((f, idx) => ({
      file: f,
      activityId: media._id,
      activityName: media.name,
      activityDate: media.created_at,
      activity: media,
      initialIndex: idx
    }));

    modalService.open(MediaModal, {
      initialMediaItems,
      readOnly,
      startFileId: files[fileIndex]?._id,
      fileUrls,
      backendService,
      media,
      setFiles: canonicalFiles => { if (ownsOrigin()) setFiles(canonicalFiles); },
      onRemovalConfirmed: () => { if (ownsOrigin()) setRemovalNotice('Photo removal confirmed.'); },
      originScopeIsCurrent: ownsOrigin,
      registerListener,
      canonicalRunRegistryRef,
      timeZone: timezone,
    });
  };

  const unmetRequirements = requirements.filter(req => !isRequirementMet(req));
  const blockedPhotoCount = files.filter(file => !arePhotoFilesEligible([file], globalConstraints)).length;
  const canSubmit = files.length > 0 && unmetRequirements.length === 0
    && !files.some(file => file.isPlaceholder)
    && (!hasPhotoRequirements || arePhotoFilesEligible(files, globalConstraints));

  return (
    <div className="media">
      {/* Requirements Section */}
      {(requirements.length > 0 || media.is_template) && (
          <div className="media-requirements-section">
              <div className="requirements-header">Required Photos</div>
              <ul className="requirements-list media-view-requirements-list">
                  {requirements.map((req, i) => {
                      const met = isRequirementMet(req);
                      const rejectionReason = met ? null : getRequirementRejectionReason(req);
                      return (
                          <li key={i} className={`requirement-item ${met ? 'met' : ''} ${rejectionReason ? 'rejected' : ''} ${editingRequirementIndex === i ? 'editing' : ''}`}>
                              {editingRequirementIndex === i ? (
                                <>
                                  <textarea
                                    className="req-edit-input"
                                    rows={3}
                                    value={editingRequirement}
                                    onChange={(event) => setEditingRequirement(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                                        event.preventDefault();
                                        saveRequirementEdit();
                                      }
                                      if (event.key === 'Escape') {
                                        event.preventDefault();
                                        cancelRequirementEdit();
                                      }
                                    }}
                                    aria-label={`Edit requirement value: ${req}`}
                                    autoFocus
                                  />
                                  <div className="req-edit-actions">
                                    <button type="button" className="req-action-btn save" onClick={saveRequirementEdit} disabled={!editingRequirement.trim() || savingRequirement} aria-label="Save requirement edit">
                                      <CheckIcon className="icon-small" />
                                    </button>
                                    <button type="button" className="req-action-btn cancel" onClick={cancelRequirementEdit} disabled={savingRequirement} aria-label="Cancel requirement edit">
                                      <XMarkIcon className="icon-small" />
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {met ? (
                                      <CheckCircleIcon className="req-icon success" />
                                  ) : (
                                      <div className="req-icon-placeholder" />
                                  )}
                                  <div className="req-copy">
                                    <span className="req-text">{req}</span>
                                    {rejectionReason && (
                                      <span className="req-rejection-reason" role="status">
                                        <strong>{rejectionReason.rejected ? 'Photo not accepted:' : 'Photo needs attention:'}</strong> {rejectionReason.reason}
                                      </span>
                                    )}
                                  </div>
                                  {media.is_template && !readOnly && (
                                    <div className="req-row-actions">
                                      <button type="button" className="req-action-btn edit" onClick={() => startRequirementEdit(i)} aria-label={`Edit requirement: ${req}`}>
                                        <PencilSquareIcon className="icon-small" />
                                      </button>
                                      <button type="button" className="req-action-btn delete" onClick={() => handleDeleteRequirement(i)} aria-label={`Delete requirement: ${req}`}>
                                        <TrashIcon className="icon-small" />
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                          </li>
                      );
                  })}
              </ul>
              {media.is_template && !readOnly && (
                  <div className="add-requirement-row">
                      <input 
                          type="text" 
                          className="req-input"
                          value={newRequirement} 
                          onChange={(e) => setNewRequirement(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddRequirement()}
                          placeholder="Describe the required photo..."
                      />
                      <button className="btn-add-req" onClick={handleAddRequirement} disabled={!newRequirement.trim()}>
                          <PlusIcon className="icon-small" />
                          <span>Add</span>
                      </button>
                  </div>
              )}
          </div>
      )}

      {globalConstraints.length > 0 && (
        <div className="media-requirements-section media-global-constraints">
          <div className="requirements-header">Applies to every photo</div>
          <ul className="requirements-list">
            {globalConstraints.map(constraint => (
              <li key={constraint} className="requirement-item">
                <span className="req-text">{constraint}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tracked Metrics Section */}
      {(trackedMetrics.length > 0 || media.is_template) && (
          <div className="media-requirements-section">
              <div className="requirements-header">Tracked Metrics</div>
              <ul className="requirements-list">
                  {trackedMetrics.map((metric, i) => (
                      <li key={i} className="requirement-item">
                          <div className="req-icon-placeholder" /> 
                          <span className="req-text">{metric}</span>
                          {media.is_template && !readOnly && (
                              <button className="req-delete-btn" onClick={() => handleDeleteTrackedMetric(i)}>
                                  <TrashIcon className="icon-small" />
                              </button>
                          )}
                      </li>
                  ))}
              </ul>
              {media.is_template && !readOnly && (
                  <div className="add-requirement-row">
                      <input 
                          type="text" 
                          className="req-input"
                          value={newTrackedMetric} 
                          onChange={(e) => setNewTrackedMetric(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddTrackedMetric()}
                          placeholder="Metric name (e.g. Waist Size)"
                      />
                      <button className="btn-add-req" onClick={handleAddTrackedMetric} disabled={!newTrackedMetric.trim()}>
                          <PlusIcon className="icon-small" />
                          <span>Add</span>
                      </button>
                  </div>
              )}
          </div>
      )}

      {hasPhotoRequirements && <p className="media-photo-rule">{globalConstraints.length > 0
        ? 'Every attached photo must validate, including additional photos.'
        : 'Required views must be verified. Unanalyzed extras are allowed; analyzed photos must validate.'}</p>}
      {hasPhotoRequirements && !readOnly && media.status !== 'complete' && blockedPhotoCount > 0 && (
        <div className="media-submission-summary" role="status">
          <strong>{unmetRequirements.length === 0 ? 'All required views are covered, but submission is blocked.' : 'Submission is blocked.'}</strong>
          <p>{blockedPhotoCount} of {files.length} attached photos still need attention.</p>
          <p>Review the photo statuses below. View details to analyze again, or remove an unwanted attachment. Nothing is removed automatically.</p>
        </div>
      )}
      {removalNotice && <p role="status">{removalNotice}</p>}
      <div className="media-files">
        {files.map((file, index) => (
          <div key={file._id} className="media-file-entry">
          <div className="media-file" onClick={() => !file.isPlaceholder && handleMediaClick(index)}>
            {loadingFiles[file._id] || file.isPlaceholder ? (
              <div className="loading-cue">{file.isPlaceholder ? 'Uploading...' : 'Loading...'}</div>
            ) : file.type.startsWith('image') ? (
              <img src={fileUrls[file._id]} alt="Uploaded" className="media-preview" />
            ) : (
              <VideoThumbnail src={fileUrls[file._id]} />
            )}
            {inferenceStatus[file._id] === 'started' && (
              <>
                  <div className="inference-analyzing-text pulsing">Analyzing...</div>
                  {file.inferences && file.inferences.length > 0 && (
                    <div className="inference-corner-sparkle"><span role="img" aria-label="sparkle">✨</span></div>
                  )}
              </>
            )}
            {inferenceStatus[file._id] === 'completed' && recentlyCompleted[file._id] && (
               <div className="inference-indicator"><span role="img" aria-label="analysis complete">✅</span> Analysis finished</div>
            )}
            {inferenceStatus[file._id] !== 'started' && !recentlyCompleted[file._id] && (inferenceStatus[file._id] === 'completed' || (file.inferences && file.inferences.length > 0)) && (
              <div className="inference-corner-sparkle"><span role="img" aria-label="sparkle">✨</span></div>
            )}
          </div>
            {hasPhotoRequirements && (() => {
              const description = describePhoto(file, photoEvidence[index], inferenceStatus[file._id], globalConstraints);
              return <div className={`media-photo-status ${description.kind} ${['attention', 'incomplete'].includes(description.kind) ? 'media-photo-rejection' : ''}`} role="status">
                <strong>{description.label}</strong>
                {description.reasons.length > 0 && <ul>{description.reasons.map(reason => <li key={reason}>{reason}</li>)}</ul>}
              </div>;
            })()}
            <div className="media-photo-actions">
              {!file.isPlaceholder && <button type="button" className="media-view-photo" onClick={() => handleMediaClick(index)} aria-label={`View details for ${file.type?.startsWith('image') ? 'photo' : 'attachment'} ${index + 1}`}>View details</button>}
              {canRemovePhotos && !file.isPlaceholder && <button type="button" className="media-remove-photo" disabled={removingFileId !== null} onClick={() => removePhoto(file)} aria-label={`Remove ${file.type?.startsWith('image') ? 'photo' : 'attachment'} ${index + 1}`}>
                <TrashIcon className="icon-small" />{removingFileId === file._id ? 'Removing…' : 'Remove'}
              </button>}
            </div>
            {removeError?.fileId === file._id && <p className="media-remove-error" role="alert">{removeError.message}</p>}
          </div>
        ))}
      </div>
      {!readOnly && isActivityOngoing(media) && !media.is_template && (
        <div className="center-button">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            capture="environment"
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          <input
            type="file"
            ref={videoInputRef}
            style={{ display: 'none' }}
            accept="video/*"
            capture="environment"
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          <input
            type="file"
            ref={attachmentInputRef}
            style={{ display: 'none' }}
            accept="image/*,video/*"
            multiple
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          <div className="media-buttons">
            <button className="btn-add media-button" onClick={() => fileInputRef.current.click()} disabled={isUploading}>
              <CameraIcon className="icon" />
            </button>
            <button className="btn-add media-button" onClick={() => videoInputRef.current.click()} disabled={isUploading}>
              <VideoCameraIcon className="icon" />
            </button>
            <button className="btn-add media-button" onClick={() => attachmentInputRef.current.click()} disabled={isUploading} title="Attach photos or videos">
              <PaperClipIcon className="icon" />
            </button>
          </div>
        </div>
      )}
      {!readOnly && (isActivityOngoing(media) || completion.pending) && !media.is_template && (
        <div className="center-button pad-button-up-small">
          <SubmitFeedbackButton
            className="btn-select"
            label={(requirements.length > 0 || globalConstraints.length > 0) && !canSubmit && files.length > 0 ? 'Fulfill Requirements' : 'Submit Files'}
            icon={<CheckIcon />}
            feedback={completion}
            noun={SUBMIT_NOUNS.media}
            onClick={() => finishActivity(media._id)}
            disabled={!canSubmit}
            style={!canSubmit && files.length > 0 ? { opacity: 0.5 } : {}}
          />
        </div>
      )}
      <SubmitFeedbackAnnouncement feedback={completion} noun={SUBMIT_NOUNS.media} />
      {/* Owner-only, and never for a template: a shared Dojo photo does not
          share the private conversation about it. */}
      {!readOnly && !media.is_template && (
        <MediaCoachChat
          key={String(media._id)}
          activityId={media._id}
          backendService={backendService}
          activityStatus={media.status}
          coachImageUrl={coachImageUrl}
          coachEmoji={coachEmoji}
          refreshKey={coachRefreshKey}
        />
      )}
    </div>
  );
};

const MEDIA_URL_DEADLINE_MS = 30000;
const MediaModal = ({ readOnly = false, initialMediaItems = [], startFileId = null, fileUrls = {}, close, backendService, media, setFiles, onRemovalConfirmed = null, originScopeIsCurrent = null, registerListener, canonicalRunRegistryRef: sharedCanonicalRunRegistryRef = null, timeZone = null }) => {
  const [mediaItems, setMediaItems] = useState(initialMediaItems);
  const [modalFileUrls, setModalFileUrls] = useState(fileUrls || {});
  // A failed or stalled original fetch is a terminal, retryable state.
  const [modalFileErrors, setModalFileErrors] = useState({});
  const [currentFileIndex, setCurrentFileIndex] = useState(() => {
    const idx = initialMediaItems.findIndex((item) => item.file?._id === startFileId);
    return idx >= 0 ? idx : 0;
  });
  const [page, setPage] = useState(1);
  const [hasMoreMedia, setHasMoreMedia] = useState(!readOnly);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [localInferenceStatus, setLocalInferenceStatus] = useState(() => {
    const status = {};
    initialMediaItems.forEach(({ file }) => {
      if (file?.inferenceStatus === 'processing') {
        status[file._id] = 'started';
      } else if (file?.inferenceStatus === 'failed') {
        status[file._id] = 'failed';
      } else if (file?.inferenceStatus === 'completed') {
        status[file._id] = 'completed';
      }
    });
    return status;
  });
  const [localInferenceResults, setLocalInferenceResults] = useState({});
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const deletingRef = useRef(null);
  const deleteScopeRef = useRef(null);
  const deleteItem = mediaItems[currentFileIndex];
  const deleteScope = { backendService, mediaId: media._id, status: media.status, readOnly, ownerId: backendService.getUser?.()?._id ?? null,
    activityId: deleteItem?.activityId, fileId: deleteItem?.file?._id, activityStatus: deleteItem?.activity?.status,
    activityOwner: deleteItem?.activity?.user_id, dueAt: deleteItem?.activity?.due_at, isTemplate: deleteItem?.activity?.is_template };
  if (!deleteScopeRef.current || Object.keys(deleteScope).some(key => deleteScope[key] !== deleteScopeRef.current[key])) deleteScopeRef.current = deleteScope;
  const activeDeleteScope = deleteScopeRef.current;
  useEffect(() => {
    deletingRef.current = null;
    setIsDeleting(false);
    setDeleteError('');
  }, [activeDeleteScope]);
  const [rerunError, setRerunError] = useState('');
  const [activeRerunActivityId, setActiveRerunActivityId] = useState(null);
  const startIndexResolvedRef = useRef(false);
  const rerunGenerationRef = useRef(0);
  const activeRerunActivityRef = useRef(null);
  const activeRunIdRef = useRef(null);
  const localCanonicalRunRegistryRef = useRef({});
  const canonicalRunByActivityRef = sharedCanonicalRunRegistryRef || localCanonicalRunRegistryRef;
  const modalMountedRef = useRef(true);

  const MEDIA_PAGE_SIZE = 5;

  const dedupeAndAppend = useCallback((incoming) => {
    setMediaItems((prev) => {
      const seen = new Set(prev.map((item) => item.file?._id));
      const merged = [...prev];
      incoming.forEach((item) => {
        const id = item?.file?._id;
        if (id && !seen.has(id)) {
          merged.push(item);
          seen.add(id);
        }
      });
      return merged;
    });
  }, []);

  const flattenMediaActivities = (activities) => {
    if (!Array.isArray(activities)) return [];
    return activities
      .filter((a) => a?.type === 'media')
      .flatMap((activity) => {
        const filesArr = activity?.data?.files || [];
        return filesArr.map((file) => ({
          file,
          activityId: activity._id,
          activityName: activity.name,
          activityDate: activity.created_at,
          activity,
        }));
      });
  };

  const fetchMediaActivitiesPage = useCallback(async (pageToFetch = 1) => {
    if (readOnly) return;
    setIsLoadingMore(true);
    try {
      const data = await backendService.fetchActivities(pageToFetch, MEDIA_PAGE_SIZE, 'media');
      const flattened = flattenMediaActivities(data?.activities || []);
      dedupeAndAppend(flattened);
      setHasMoreMedia(pageToFetch < (data?.totalPages || 0));
      setPage(pageToFetch);

      // If we haven't yet located the starting file, try to set it now
      if (startFileId) {
        setCurrentFileIndex((prevIdx) => {
          const foundIdx = flattened.findIndex((item) => item.file?._id === startFileId);
          if (foundIdx >= 0) {
            // found in the newly appended portion; compute its absolute index
            return prevIdx >= 0 && prevIdx < mediaItems.length ? prevIdx : mediaItems.length + foundIdx;
          }
          return prevIdx;
        });
      }
    } catch (error) {
      console.error('Failed to fetch media activities', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [readOnly, MEDIA_PAGE_SIZE, backendService, dedupeAndAppend, mediaItems.length, startFileId]);

  useEffect(() => {
    fetchMediaActivitiesPage(1);
  }, [fetchMediaActivitiesPage]);

  useEffect(() => {
    if (!startFileId || startIndexResolvedRef.current) {
      return;
    }
    const foundIdx = mediaItems.findIndex((item) => item.file?._id === startFileId);
    if (foundIdx >= 0) {
      setCurrentFileIndex(foundIdx);
      startIndexResolvedRef.current = true;
    }
  }, [mediaItems, startFileId]);

  useEffect(() => {
    // Prevent out-of-bounds after deletions or when new data shrinks list
    if (currentFileIndex >= mediaItems.length && mediaItems.length > 0) {
      setCurrentFileIndex(mediaItems.length - 1);
    }
  }, [currentFileIndex, mediaItems.length]);

  useEffect(() => {
    // Seed inference status for newly loaded items
    setLocalInferenceStatus((prev) => {
      const next = { ...prev };
      mediaItems.forEach(({ file }) => {
        if (!file) return;
        if (next[file._id]) return;
        if (file.inferenceStatus === 'processing') {
          next[file._id] = 'started';
        } else if (file.inferenceStatus === 'failed') {
          next[file._id] = 'failed';
        } else if (file.inferenceStatus === 'completed') {
          next[file._id] = 'completed';
        }
      });
      return next;
    });
  }, [mediaItems]);

  const handlePrevious = useCallback(() => {
    if (deletingRef.current) return;
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
  }, [currentFileIndex]);

  const handleNext = useCallback(async () => {
    if (deletingRef.current) return;
    const lastIndex = mediaItems.length - 1;
    if (currentFileIndex < lastIndex) {
      setCurrentFileIndex(currentFileIndex + 1);
      return;
    }

    if (hasMoreMedia && !isLoadingMore) {
      await fetchMediaActivitiesPage(page + 1);
      if (deletingRef.current) return;
      setCurrentFileIndex((idx) => idx + 1);
    }
  }, [currentFileIndex, mediaItems.length, hasMoreMedia, isLoadingMore, fetchMediaActivitiesPage, page]);

  useEffect(() => {
    const currentItem = mediaItems[currentFileIndex];
    if (!currentItem || !currentItem.file?._id) return;
    const fileId = currentItem.file._id;
    if (modalFileUrls[fileId] || modalFileErrors[fileId]) return;

    let cancelled = false;
    withRequestDeadline(backendService.fetchMediaFileUrl(currentItem.activityId, fileId, null), MEDIA_URL_DEADLINE_MS, 'Media request timed out.')
      .then((url) => {
        if (cancelled) return;
        if (url) setModalFileUrls((prev) => ({ ...prev, [fileId]: url }));
        else setModalFileErrors((prev) => ({ ...prev, [fileId]: true }));
      })
      .catch((err) => {
        console.error('Failed to fetch media file url', err);
        if (!cancelled) setModalFileErrors((prev) => ({ ...prev, [fileId]: true }));
      });

    return () => { cancelled = true; };
  }, [backendService, mediaItems, currentFileIndex, modalFileUrls, modalFileErrors]);

  const handleDelete = async () => {
    if (!showDelete || deletingRef.current) return;
    if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
    const currentItem = mediaItems[currentFileIndex];
    const fileToDelete = currentItem?.file;
    if (!fileToDelete) return;
    const scope = deleteScopeRef.current;
    const isCurrent = () => modalMountedRef.current && scope === deleteScopeRef.current
      && (backendService.getUser?.()?._id ?? null) === scope.ownerId
      && (!originScopeIsCurrent || originScopeIsCurrent());
    if (!isCurrent()) return;
    const token = {};
    deletingRef.current = token;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const outcome = await removeAndConfirm(backendService, currentItem.activityId, fileToDelete._id, isCurrent);
      if (!isCurrent()) return;
      if (!outcome) {
        setDeleteError('Removal status could not be confirmed. Refresh this activity before trying again.');
        return;
      }
      const updatedMedia = outcome.activity;
      if (String(currentItem.activityId) === String(media._id) && setFiles) setFiles(updatedMedia.data.files);
      if (!outcome.removed) {
        setDeleteError('Could not remove this photo. Please try again.');
        return;
      }
      const canonicalById = new Map(updatedMedia.data.files.map(file => [String(file._id), file]));
      setMediaItems(prev => prev.filter(item => String(item.activityId) !== String(currentItem.activityId) || canonicalById.has(String(item.file._id)))
        .map(item => String(item.activityId) === String(currentItem.activityId) ? { ...item, activity: updatedMedia, file: canonicalById.get(String(item.file._id)) } : item));
      onRemovalConfirmed?.();
      close();
    } finally {
      if (deletingRef.current === token) {
        deletingRef.current = null;
        if (modalMountedRef.current) setIsDeleting(false);
      }
    }
  };

  const handleDownload = async () => {
    const currentItem = mediaItems[currentFileIndex];
    const currentFile = currentItem?.file;
    if (!currentFile) return;
    try {
      const blob = await backendService.fetchMediaFileBlob(currentItem.activityId, currentFile._id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentFile.name || `download-${currentFile._id}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download failed", err);
      alert("Failed to download file.");
    }
  };

  const applyCanonicalActivity = useCallback((activity) => {
    const refreshedFiles = activity?.data?.files || [];
    if (String(activity?._id) === String(media?._id) && setFiles) {
      setFiles(refreshedFiles);
    }
    if (!modalMountedRef.current) return;
    const refreshedById = new Map(refreshedFiles.map(file => [String(file._id), file]));
    setMediaItems(prev => prev.map(item => {
      if (String(item.activityId) !== String(activity?._id)) return item;
      const refreshedFile = refreshedById.get(String(item.file?._id));
      return refreshedFile ? { ...item, activity, file: refreshedFile } : item;
    }));
    setLocalInferenceStatus(prev => {
      const next = { ...prev };
      refreshedFiles.forEach(file => {
        next[file._id] = file.inferenceStatus === 'processing' ? 'started' : file.inferenceStatus;
      });
      return next;
    });
    setLocalInferenceResults(prev => {
      const next = { ...prev };
      refreshedFiles.forEach(file => {
        const latest = file.inferences?.[file.inferences.length - 1]?.result;
        if (latest) next[file._id] = latest;
      });
      return next;
    });
  }, [media?._id, setFiles]);

  // A rerun re-scores every image in the activity, so no file scoping here.
  const waitForCanonicalRerun = useCallback(({ activityId, owner }) => waitForCanonicalRun({
    activityId,
    owner,
    registryRef: canonicalRunByActivityRef,
    backendService,
    applyActivity: applyCanonicalActivity,
  }), [applyCanonicalActivity, backendService, canonicalRunByActivityRef]);

  const handleReRun = async (e) => {
    e.stopPropagation();
    if (readOnly) return;
    const currentItem = mediaItems[currentFileIndex];
    const currentFile = currentItem?.file;
    if (!currentFile) return;
    if (activeRerunActivityRef.current) return;
    const generation = rerunGenerationRef.current + 1;
    rerunGenerationRef.current = generation;
    activeRerunActivityRef.current = String(currentItem.activityId);
    setActiveRerunActivityId(String(currentItem.activityId));
    setRerunError('');
    const {
      key: activityKey,
      previous: previousCanonicalOwner,
      pending: pendingCanonicalOwner,
    } = beginCanonicalRun(canonicalRunByActivityRef, currentItem.activityId);
    let canonicalOwner = null;
    try {
      const triggerResult = await withRequestDeadline(
        backendService.triggerInference(currentItem.activityId, currentFile._id, { timeout: 12000 }),
        13000,
        'The analysis request timed out. Please try again.',
      );
      if (generation !== rerunGenerationRef.current) {
        abandonCanonicalRun(canonicalRunByActivityRef, activityKey, pendingCanonicalOwner, previousCanonicalOwner);
        return;
      }
      const runId = triggerResult?.run_id;
      if (!runId) throw new Error('The analysis started without a tracking ID. Please try again.');
      canonicalOwner = confirmCanonicalRun(canonicalRunByActivityRef, activityKey, pendingCanonicalOwner, runId);
      if (!canonicalOwner) throw new Error('A newer analysis request replaced this one.');
      activeRunIdRef.current = runId;
      setLocalInferenceStatus(prev => {
        const next = { ...prev };
        mediaItems.forEach(item => {
          if (String(item.activityId) === String(currentItem.activityId) && item.file?._id) {
            next[item.file._id] = 'started';
          }
        });
        return next;
      });
      const reconciliation = waitForCanonicalRerun({
        activityId: currentItem.activityId,
        owner: canonicalOwner,
      });
      reconciliation.then(() => {
        if (!releaseCanonicalRun(canonicalRunByActivityRef, activityKey, canonicalOwner)) return;
        if (modalMountedRef.current) setRerunError('');
      }).catch(error => {
        if (!releaseCanonicalRun(canonicalRunByActivityRef, activityKey, canonicalOwner)) return;
        if (modalMountedRef.current) {
          setRerunError(error.message || 'Failed to refresh the completed analysis.');
        }
      });
      await withRequestDeadline(
        reconciliation,
        60000,
        'Analysis is still running. Results will refresh automatically when ready.',
      );
    } catch (error) {
      console.error('Error triggering inference:', error);
      if (!canonicalOwner) {
        abandonCanonicalRun(canonicalRunByActivityRef, activityKey, pendingCanonicalOwner, previousCanonicalOwner);
      }
      if (generation === rerunGenerationRef.current) {
        setRerunError(error.message || 'Failed to refresh the analysis.');
        setLocalInferenceStatus(prev => {
          const next = { ...prev };
          mediaItems.forEach(item => {
            if (String(item.activityId) !== String(currentItem.activityId) || !item.file?._id) return;
            const fileStatus = item.file.inferenceStatus;
            next[item.file._id] = fileStatus === 'processing' ? null : fileStatus;
          });
          return next;
        });
      }
    } finally {
      if (generation === rerunGenerationRef.current) {
        activeRerunActivityRef.current = null;
        activeRunIdRef.current = null;
        setActiveRerunActivityId(null);
      }
    }
  };

  useEffect(() => () => {
    modalMountedRef.current = false;
    rerunGenerationRef.current += 1;
    activeRerunActivityRef.current = null;
    activeRunIdRef.current = null;
  }, []);

  useEffect(() => {
    if (registerListener) {
      return registerListener((message) => {
        if (
          activeRunIdRef.current
          && String(message.activityId) === activeRerunActivityRef.current
          && (message.status === 'completed' || message.status === 'failed')
          && (message.data?._session?.run_id || message.run_id) !== activeRunIdRef.current
        ) return;
        setLocalInferenceStatus(prev => ({ ...prev, [message.fileId]: message.status }));
        if (message.status === 'completed') {
          setLocalInferenceResults(prev => ({ ...prev, [message.fileId]: message.data }));
        }
      });
    }
  }, [registerListener]);

  const currentItem = mediaItems[currentFileIndex];
  const currentFile = currentItem?.file;
  const status = currentFile ? localInferenceStatus[currentFile._id] : null;
  const latestResult = currentFile ? localInferenceResults[currentFile._id] : null;
  const storedResult = currentFile && currentFile.inferences && currentFile.inferences.length > 0 
    ? currentFile.inferences[currentFile.inferences.length - 1].result 
    : null;
  
  const resultToShow = sanitizeBodyEstimates(latestResult || storedResult, currentFile?._id);

  const toPercentage = (val) => {
    if (val === undefined || val === null) return null;
    return val > 1.0 ? val.toFixed(1) : (val * 100).toFixed(1);
  };

  const bodyFat = resultToShow ? (resultToShow.estimated_body_fat_range || resultToShow.estimated_body_fat) : null;
  const bodyFatRange = (bodyFat && bodyFat.lower_bound !== undefined && bodyFat.upper_bound !== undefined)
    ? `${toPercentage(bodyFat.lower_bound)}–${toPercentage(bodyFat.upper_bound)}%`
    : null;
  const bestEstimate = (bodyFat && bodyFat.best_estimate !== undefined)
    ? `${toPercentage(bodyFat.best_estimate)}%`
    : null;
  const musculature = resultToShow?.musculature_assessment?.overall_index;
  const confidence = resultToShow?.confidence;
  const fatCues = resultToShow?.primary_visual_cues || [];
  const muscleCues = resultToShow?.musculature_assessment?.primary_musculature_cues || [];
  const customMetrics = resultToShow?.custom_metrics && Object.keys(resultToShow.custom_metrics).length > 0
    ? Object.entries(resultToShow.custom_metrics)
    : [];

  const displayedActivity = String(currentItem?.activityId) === String(media._id) ? media : currentItem?.activity;
  const viewerContract = resolveMediaRequirementContract(displayedActivity?.data || {});
  const viewerFile = currentFile && { ...currentFile, inferenceStatus: status === 'started' ? 'processing' : status || currentFile.inferenceStatus,
    ...(latestResult ? { inferences: [{ result: latestResult }] } : {}) };
  const viewerPhotoStatus = viewerFile && (viewerContract.views.length || viewerContract.globalConstraints.length)
    ? describePhoto(viewerFile, getFilePhotoEvidence(viewerFile, viewerContract.globalConstraints, displayedActivity?.status), status, viewerContract.globalConstraints) : null;
  const viewerOwnerId = backendService.getUser?.()?._id;
  const showDelete = !readOnly && (!originScopeIsCurrent || originScopeIsCurrent()) && !!currentFile && !!displayedActivity && !displayedActivity.is_template
    && isActivityOngoing(displayedActivity)
    && (viewerOwnerId == null || displayedActivity.user_id == null || String(viewerOwnerId) === String(displayedActivity.user_id));
  const isAnalyzing = status === 'started'
    || (currentItem && String(currentItem.activityId) === activeRerunActivityId);

  const currentUrl = currentFile ? modalFileUrls[currentFile._id] : null;
  return (
    <MediaGalleryStage
      title={currentItem?.activityName || media.name || 'Media Activity'}
      date={currentItem?.activityDate || media.created_at}
      timeZone={timeZone}
      kind={currentFile?.type?.startsWith('image') ? 'image' : 'video'}
      url={currentUrl}
      alt={currentFile?.name || 'media item'}
      status={currentUrl ? 'ready' : currentFile && modalFileErrors[currentFile._id] ? 'error' : 'loading'}
      error="This attachment could not be loaded."
      onRetry={() => setModalFileErrors(prev => { const next = { ...prev }; delete next[currentFile._id]; return next; })}
      index={currentFileIndex}
      count={mediaItems.length}
      canPrevious={currentFileIndex > 0}
      canNext={hasMoreMedia || currentFileIndex < mediaItems.length - 1}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onClose={close}
      onDownload={handleDownload}
      onDelete={showDelete ? handleDelete : null}
      busy={isDeleting}
      notices={<>
        {viewerPhotoStatus && <div className={`media-photo-status ${viewerPhotoStatus.kind} ${['attention', 'incomplete'].includes(viewerPhotoStatus.kind) ? 'media-photo-rejection' : ''}`} role="status">
          <strong>{viewerPhotoStatus.label}</strong>
          {viewerPhotoStatus.reasons.length > 0 && <ul>{viewerPhotoStatus.reasons.map(reason => <li key={reason}>{reason}</li>)}</ul>}
        </div>}
        {!readOnly && currentFile?.type?.startsWith('image') && <p className="media-photo-rule">Running analysis rechecks all photos in this activity, not just the displayed photo.</p>}
        {isDeleting && <p className="media-removal-progress" role="status">Removing photo…</p>}
        {deleteError && <p className="media-remove-error" role="alert">{deleteError}</p>}
      </>}
    >
        {currentFile?.type?.startsWith('image') && (
          <div className="mm-analysis" onClick={(e) => e.stopPropagation()}>
            {rerunError && <div className="mm-analysis-error" role="alert">{rerunError}</div>}
            {resultToShow ? (
              <>
                <div className="mm-analysis-head">
                  <div className="mm-analysis-title">
                    <SparklesIcon className="mm-analysis-title-icon" />
                    <span>Body Composition Analysis</span>
                  </div>
                  {!readOnly && (<button
                    className="mm-rerun"
                    onClick={handleReRun}
                    disabled={isAnalyzing}
                    title="Re-run analysis"
                  >
                    <ArrowPathIcon className={isAnalyzing ? 'mm-spin' : ''} />
                    <span>{isAnalyzing ? 'Running…' : 'Re-run all photos'}</span>
                  </button>)}
                </div>

                <div className="mm-stat-grid">
                  {bodyFatRange && (
                    <div className="mm-stat mm-stat-primary">
                      <div className="mm-stat-label">Body Fat</div>
                      <div className="mm-stat-value">{bodyFatRange}</div>
                      {bestEstimate && <div className="mm-stat-sub">Est. {bestEstimate}</div>}
                    </div>
                  )}
                  {musculature !== undefined && (
                    <div className="mm-stat">
                      <div className="mm-stat-label">Musculature</div>
                      <div className="mm-stat-value">{musculature.toFixed(1)}<span className="mm-stat-unit">/10</span></div>
                    </div>
                  )}
                  {confidence && (
                    <div className="mm-stat">
                      <div className="mm-stat-label">Confidence</div>
                      <div className={`mm-stat-value mm-confidence mm-confidence-${String(confidence).toLowerCase()}`}>
                        {confidence}
                      </div>
                    </div>
                  )}
                </div>

                {(fatCues.length > 0 || muscleCues.length > 0) && (
                  <div className="mm-cue-groups">
                    {fatCues.length > 0 && (
                      <div className="mm-cue-group">
                        <div className="mm-cue-label">Fat indicators</div>
                        <div className="mm-cue-chips">
                          {fatCues.map((cue, i) => (
                            <span key={`f-${i}`} className="mm-chip mm-chip-fat">{cue}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {muscleCues.length > 0 && (
                      <div className="mm-cue-group">
                        <div className="mm-cue-label">Muscle indicators</div>
                        <div className="mm-cue-chips">
                          {muscleCues.map((cue, i) => (
                            <span key={`m-${i}`} className="mm-chip mm-chip-muscle">{cue}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {customMetrics.length > 0 && (
                  <div className="mm-custom-metrics">
                    <div className="mm-custom-metrics-label">Custom metrics</div>
                    <dl className="mm-custom-metrics-list">
                      {customMetrics.map(([key, val]) => (
                        <div key={key} className="mm-custom-row">
                          <dt>{key}</dt>
                          <dd>{typeof val === 'number' ? val.toFixed(1) : String(val)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
              </>
            ) : (
              <div className="mm-analysis-empty">
                <SparklesIcon className="mm-analysis-empty-icon" />
                <div className="mm-analysis-empty-copy">
                  <div className="mm-analysis-empty-title">
                    {isAnalyzing ? 'Analyzing your photo…' : 'No analysis yet'}
                  </div>
                  <div className="mm-analysis-empty-sub">
                    {isAnalyzing
                      ? 'This usually takes about a minute.'
                      : readOnly ? 'Analysis is unavailable for this photo.' : 'Run body-composition analysis on all photos in this activity.'}
                  </div>
                </div>
                {!readOnly && (<button
                  className="mm-cta"
                  onClick={handleReRun}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? 'Running…' : 'Run Analysis'}
                </button>)}
              </div>
            )}
          </div>
        )}
    </MediaGalleryStage>
  );
};

export { MediaModal };
export default Media;
